import type { IUiThreeDPresentation } from './Interfaces';
import bodyModelUrl from '@/components/cubeControl/body.drc?url';
import type { IPresentationChangeHandler } from './Interfaces';
import { DecodeDracoUrlToBufferGeometry } from './../Utilities/DracoUtils';
import * as THREE from 'three';
import type { ICameraController, ICameraInfoApi, IViewPortController } from '@/engine/app/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';

export enum SceneCorner {
  LeftTop = 0,
  LeftBottom = 1,
  RightTop = 2,
  RightBottom = 3
}

class HumanMaterial extends THREE.MeshLambertMaterial {
  public readonly clipping = false;

  public readonly isShaderMaterial = true;

  public readonly uniformsGroups = [];

  public constructor(parameters?: THREE.MeshLambertMaterialParameters) {
    super(parameters);
  }
}

export class HumanPresentation implements IUiThreeDPresentation {
  private readonly _mesh: THREE.Mesh;

  private readonly _changeHandler: IPresentationChangeHandler;

  private readonly _cameraController: ICameraController;

  private readonly _corner: SceneCorner;

  private readonly _minOffset: THREE.Vector2;

  private readonly _maxOffset: THREE.Vector2;

  private _isMaximized: boolean;

  private readonly _minHumanRadius: number;

  private readonly _maxHumanRadius: number;

  private readonly _subscriptions = new DisposablesBasket();

  public constructor(
    corner: SceneCorner,
    minHumanRadius: number,
    maxHumanRadius: number,
    minOffset: THREE.Vector2,
    maxOffset: THREE.Vector2,
    changeHandler: IPresentationChangeHandler,
    viewPortController: IViewPortController,
    bodyMeshColor: THREE.Color,
    opacity?: number
  ) {
    this._corner = corner;
    this._minHumanRadius = minHumanRadius;
    this._maxHumanRadius = maxHumanRadius;
    this._minOffset = minOffset;
    this._maxOffset = maxOffset;
    this._isMaximized = viewPortController.isMaximized();

    this._changeHandler = changeHandler;
    this._cameraController = viewPortController.getCameraController();

    this._mesh = new THREE.Mesh();
    this._mesh.material = new HumanMaterial({
      color: bodyMeshColor,
      transparent: opacity !== undefined,
      opacity: opacity ?? 1.0,
      side: opacity !== undefined ? THREE.DoubleSide : THREE.FrontSide
    });
    this._mesh.matrixAutoUpdate = false;
    this._mesh.renderOrder = 1;
    this._mesh.frustumCulled = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    HumanPresentation._loadHumanGeometry((geometry) => this._onGeometryLoaded(geometry));

    this._cameraController.onCameraModified((cameraInfo: ICameraInfoApi): void => {
      this._setHumanPosition(cameraInfo);
    });

    this._subscriptions.add(viewPortController.trackMaximizedChanged(this._onMaximizedChanged.bind(this)));
  }

  private _onGeometryLoaded(bodyGeometry: THREE.BufferGeometry): void {
    this._mesh.geometry = bodyGeometry;
    this._setHumanPosition(this._cameraController);
    this._changeHandler.handlePresentationChange();
  }

  public get3dObject(): THREE.Object3D {
    return this._mesh;
  }

  public dispose(): void {
    this._mesh.geometry.dispose();
    this._subscriptions.dispose();
  }

  private _onMaximizedChanged(isMaximized: boolean): void {
    this._isMaximized = isMaximized;
    this._setHumanPosition(this._cameraController);
  }

  private _setHumanPosition(cameraInfo: ICameraInfoApi): void {
    if (!this._mesh.geometry.boundingSphere) return;
    const cam = cameraInfo.getCameraClone();
    const pos = cameraInfo.getCameraPosition();
    const dimension = this._cameraController.getFrameDimensions();
    const pixelRatio = (2 * cam.right) / dimension.x;
    const geometryRadius = this._mesh.geometry.boundingSphere.radius;
    const desiredHumanRadius = this._isMaximized ? this._maxHumanRadius : this._minHumanRadius;
    const cornerOffset = this._isMaximized ? this._maxOffset : this._minOffset;
    const scaleFactor = (desiredHumanRadius * pixelRatio) / geometryRadius;
    const zoomFactor = 1 / cam.zoom;
    const sceneOffset = this._calculateOffset(cam, desiredHumanRadius, cornerOffset, pixelRatio);
    const shiftVector = new THREE.Vector3(
      zoomFactor * sceneOffset.x,
      -zoomFactor * sceneOffset.y,
      -zoomFactor * desiredHumanRadius * 2
    ).applyQuaternion(cam.quaternion);
    const meshScaleFactor = zoomFactor * scaleFactor;
    this._mesh.matrix.makeScale(meshScaleFactor, meshScaleFactor, meshScaleFactor);
    this._mesh.matrix.setPosition(pos.x + shiftVector.x, pos.y + shiftVector.y, pos.z + shiftVector.z);
  }

  private _calculateOffset(
    camera: THREE.OrthographicCamera,
    humanRadius: number,
    offset: THREE.Vector2,
    ratio: number
  ): THREE.Vector2 {
    if (this._corner === SceneCorner.LeftTop) {
      return new THREE.Vector2(
        camera.left + ratio * (humanRadius + offset.x),
        camera.bottom + ratio * (humanRadius + offset.y)
      );
    }
    if (this._corner === SceneCorner.LeftBottom) {
      return new THREE.Vector2(
        camera.left + ratio * (humanRadius + offset.x),
        camera.top - ratio * (humanRadius + offset.y)
      );
    }
    if (this._corner === SceneCorner.RightTop) {
      return new THREE.Vector2(
        camera.right - ratio * (humanRadius + offset.x),
        camera.bottom + ratio * (humanRadius + offset.y)
      );
    }
    if (this._corner === SceneCorner.RightBottom) {
      return new THREE.Vector2(
        camera.right - ratio * (humanRadius + offset.x),
        camera.top - ratio * (humanRadius + offset.y)
      );
    }
    return new THREE.Vector2();
  }

  private static async _loadHumanGeometry(onReady: (geometry: THREE.BufferGeometry) => void): Promise<void> {
    const bodyGeometry = await DecodeDracoUrlToBufferGeometry(bodyModelUrl);
    bodyGeometry.computeBoundingSphere();
    if (!bodyGeometry.boundingSphere) {
      throw new Error('BoundingSphere generation failed');
    }

    const center = bodyGeometry.boundingSphere.center;
    bodyGeometry.applyMatrix4(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));

    onReady(bodyGeometry);
  }
}
